/**
 * Overview tab for the roleplay results page — brought to parity with the AIQM
 * detail Overview (training-aiqm-detail/index.tsx), which has the same three
 * blocks: Conversation Flow, Rubric Score, and Action Items.
 *
 * Reuses the shared `RubricBar` + `rubricGradeBand` + `priorityColor` so it
 * looks identical to AIQM without modifying the AIQM page. `conversation_flow_
 * assessment` and `action_items` are not emitted for roleplay sessions yet, so
 * those blocks render graceful empty states until the BE ships them (handoff:
 * handoff-docs/18-06-2026-training-results-overview-fields.md).
 */
import { ArrowRightIcon } from "lucide-react";
import { RubricBar, rubricGradeBand } from "@component/training/rubric-bar";
import { KnowledgeSourcesSection } from "@component/training/knowledge-sources-section";
import { cn } from "@util/tailwind";
import {
    RUBRIC_LABELS,
    type KnowledgeManifest,
    type RubricScores,
    type RubricCriterion,
    type TrainingActionItem,
} from "@type/training";
import { priorityColor } from "@type/aiqm";

function SectionHeader({ title, badge }: { title: string; badge?: string }) {
    return (
        <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</p>
            {badge && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                    {badge}
                </span>
            )}
        </div>
    );
}

/** "Rubric Score" mini-section — mirrors AIQM's CompactRubricSection (bars +
 *  total/100 + grade band). The AIQM "View Rubric" drawer is intentionally
 *  omitted here (it's AIQM-page-local); the per-criterion detail still shows in
 *  the dimension tabs. */
function RubricScoreSection({
    scores,
    total,
    criteria,
}: {
    scores: RubricScores;
    total: number | null;
    criteria?: RubricCriterion[];
}) {
    const computedTotal =
        typeof total === "number"
            ? total
            : Object.values(scores).reduce((sum, v) => sum + v, 0);
    const band = rubricGradeBand(computedTotal);
    const rubricKeys = Object.keys(RUBRIC_LABELS) as (keyof RubricScores)[];
    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Rubric Score
                </p>
                <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-gray-900 tabular-nums">
                        {computedTotal}
                        <span className="text-gray-400 font-normal">/100</span>
                    </span>
                    <span
                        className={cn(
                            "inline-flex items-center px-2 py-0.5 border rounded-full text-[10px] font-semibold",
                            band.cls,
                        )}
                    >
                        {band.label}
                    </span>
                </div>
            </div>
            <div>
                {rubricKeys.map((key) => {
                    const criterion = criteria?.find(
                        (c) => c.name.toLowerCase() === (RUBRIC_LABELS[key] ?? key).toLowerCase(),
                    );
                    return (
                        <RubricBar
                            key={key}
                            label={criterion?.name ?? RUBRIC_LABELS[key]}
                            value={scores[key]}
                        />
                    );
                })}
            </div>
        </div>
    );
}

/** Action items list — mirrors AIQM's ActionItemsList (priority-sorted rows). */
function ActionItemsList({ items }: { items: TrainingActionItem[] }) {
    const humanizeCategory = (category: string) =>
        category
            .split("_")
            .filter(Boolean)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(" ");
    const order: Record<string, number> = { high: 0, medium: 1, low: 2 };
    const sorted = [...items].sort(
        (a, b) => (order[a.priority] ?? 99) - (order[b.priority] ?? 99),
    );
    return (
        <div className="space-y-2">
            {sorted.map((item, i) => {
                const colors = priorityColor(item.priority);
                return (
                    <div key={i} className="border border-gray-100 rounded-lg p-3">
                        <div className="flex items-start gap-2.5">
                            <ArrowRightIcon className={cn("w-4 h-4 flex-shrink-0 mt-0.5", colors.text)} />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-800">
                                    <span className={cn("font-semibold capitalize mr-1", colors.text)}>
                                        {item.priority}:
                                    </span>
                                    {item.description}
                                </p>
                                {item.technical_suggestion && (
                                    <p className="text-xs text-gray-500 mt-1.5">
                                        {item.technical_suggestion}
                                    </p>
                                )}
                                {(item.location || item.category) && (
                                    <div className="flex gap-3 mt-1.5 text-xs text-gray-400">
                                        {item.location && <span>@ {item.location}</span>}
                                        {item.category && <span>{humanizeCategory(item.category)}</span>}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/** Muted placeholder for an Overview block whose BE data isn't available yet. */
function PendingBlock({ children }: { children: React.ReactNode }) {
    return (
        <p className="text-sm text-gray-400 rounded-lg border border-dashed border-gray-200 px-4 py-5 text-center">
            {children}
        </p>
    );
}

export interface ResultsOverviewTabProps {
    conversationFlow: string | null | undefined;
    rubricScores: RubricScores | null;
    rubricTotal: number | null;
    rubricCriteria?: RubricCriterion[];
    actionItems: TrainingActionItem[] | null | undefined;
    /** Scoring-time knowledge snapshot — drives the Sources section (employer
     *  view only; absent on legacy results, in which case nothing renders). */
    knowledgeManifest?: KnowledgeManifest | null;
    showSources?: boolean;
}

export function ResultsOverviewTab({
    conversationFlow,
    rubricScores,
    rubricTotal,
    rubricCriteria,
    actionItems,
    knowledgeManifest,
    showSources = false,
}: ResultsOverviewTabProps) {
    return (
        <div className="space-y-6 px-5 pt-3 pb-4">
            {/* Conversation Flow */}
            <div>
                <SectionHeader title="Conversation Flow" />
                {conversationFlow ? (
                    <p className="text-sm text-gray-700 leading-relaxed">{conversationFlow}</p>
                ) : (
                    <PendingBlock>
                        Conversation flow analysis isn't available for roleplay sessions yet.
                    </PendingBlock>
                )}

                {rubricScores && (
                    <div className="mt-5 pt-4 border-t border-gray-100">
                        <RubricScoreSection
                            scores={rubricScores}
                            total={rubricTotal}
                            criteria={rubricCriteria}
                        />
                    </div>
                )}
            </div>

            {/* Action Items */}
            <div>
                <SectionHeader
                    title="Action Items"
                    badge={actionItems && actionItems.length > 0 ? `${actionItems.length} identified` : undefined}
                />
                {actionItems && actionItems.length > 0 ? (
                    <ActionItemsList items={actionItems} />
                ) : (
                    <PendingBlock>
                        Action items aren't generated for roleplay sessions yet.
                    </PendingBlock>
                )}
            </div>

            {/* Sources — same section styling as the blocks above */}
            {showSources && <KnowledgeSourcesSection manifest={knowledgeManifest} />}
        </div>
    );
}
